import { CertificationCriteria, CertificationLevel, StudentCertificationProgress, StudentRequirementProgress, CertificateMetadata, ProgramPrerequisite, CertificationStatus, StatusChangeRecord } from '@/types/certification';
import { v4 as uuidv4 } from 'uuid';
import { safeFormatDate } from '@/services/db/types';
import { trackCertificateIssuance, trackStatusChange } from '@/utils/certification/certificateHistoryUtils';

/**
 * In-memory storage for certification criteria for programs
 * In a real app, this would be stored in a database
 */
const certificationCriteriaStore: Record<string, CertificationCriteria> = {};

/**
 * In-memory storage for student certification progress
 * In a real app, this would be stored in a database
 */
const studentCertificationProgressStore: Record<string, StudentCertificationProgress[]> = {};

/**
 * In-memory storage for issued certificates
 * In a real app, this would be stored in a database
 */
const issuedCertificatesStore: Record<string, CertificateMetadata[]> = {};

/**
 * Default certification requirements by program category
 */
const defaultRequirementsByCategory: Record<string, CertificationCriteria> = {
  'DIT Programs': {
    programTitle: '*',
    programCategory: 'DIT Programs',
    requirements: [
      {
        id: 'attendance',
        type: 'attendance',
        description: 'Minimum class attendance',
        minimumValue: 80,
        maxValue: 100,
        optional: false,
        weight: 0.4
      },
      {
        id: 'assessments',
        type: 'assessment',
        description: 'Practical assignments completion',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.4
      },
      {
        id: 'final-exam',
        type: 'exam',
        description: 'Final exam score',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.2
      }
    ],
    passingThreshold: 60,
    meritThreshold: 75,
    distinctionThreshold: 90
  },
  'Programming Languages': {
    programTitle: '*',
    programCategory: 'Programming Languages',
    requirements: [
      {
        id: 'attendance',
        type: 'attendance',
        description: 'Minimum class attendance',
        minimumValue: 70,
        maxValue: 100,
        optional: false,
        weight: 0.2
      },
      {
        id: 'coding-assignments',
        type: 'assessment',
        description: 'Coding assignments completion',
        minimumValue: 70,
        maxValue: 100,
        optional: false,
        weight: 0.3
      },
      {
        id: 'project',
        type: 'project',
        description: 'Final project implementation',
        minimumValue: 70,
        maxValue: 100,
        optional: false,
        weight: 0.3
      },
      {
        id: 'final-exam',
        type: 'exam',
        description: 'Final exam score',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.2
      }
    ],
    passingThreshold: 65,
    meritThreshold: 80,
    distinctionThreshold: 90
  },
  'Language': {
    programTitle: '*',
    programCategory: 'Language',
    requirements: [
      {
        id: 'attendance',
        type: 'attendance',
        description: 'Class attendance',
        minimumValue: 80,
        maxValue: 100,
        optional: false,
        weight: 0.2
      },
      {
        id: 'speaking',
        type: 'assessment',
        description: 'Speaking assessment',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.25
      },
      {
        id: 'writing',
        type: 'assessment',
        description: 'Writing assessment',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.25
      },
      {
        id: 'final-exam',
        type: 'exam',
        description: 'Comprehensive exam',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.3
      }
    ],
    passingThreshold: 60,
    meritThreshold: 75,
    distinctionThreshold: 90
  },
  'English File': {
    programTitle: '*',
    programCategory: 'English File',
    requirements: [
      {
        id: 'attendance',
        type: 'attendance',
        description: 'Class attendance',
        minimumValue: 80,
        maxValue: 100,
        optional: false,
        weight: 0.2
      },
      {
        id: 'speaking',
        type: 'assessment',
        description: 'Speaking assessment',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.25
      },
      {
        id: 'writing',
        type: 'assessment',
        description: 'Writing assessment',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.25
      },
      {
        id: 'final-exam',
        type: 'exam',
        description: 'Comprehensive exam',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.3
      }
    ],
    passingThreshold: 60,
    meritThreshold: 75,
    distinctionThreshold: 90,
    prerequisites: [
      {
        programTitle: '*',
        programCategory: 'English File',
        requiredLevel: 'pass'
      }
    ]
  },
  'default': {
    programTitle: '*',
    programCategory: 'default',
    requirements: [
      {
        id: 'attendance',
        type: 'attendance',
        description: 'Class attendance',
        minimumValue: 75,
        maxValue: 100,
        optional: false,
        weight: 0.3
      },
      {
        id: 'assessments',
        type: 'assessment',
        description: 'Practical assessments',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.4
      },
      {
        id: 'final-exam',
        type: 'exam',
        description: 'Final examination',
        minimumValue: 60,
        maxValue: 100,
        optional: false,
        weight: 0.3
      }
    ],
    passingThreshold: 60,
    meritThreshold: 75,
    distinctionThreshold: 90
  }
};

/**
 * Special programs with prerequisites
 */
const programPrerequisites: Record<string, ProgramPrerequisite[]> = {
  'File Two': [
    { 
      programTitle: 'File One', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Three': [
    { 
      programTitle: 'File Two', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Four': [
    { 
      programTitle: 'File Three', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Five': [
    { 
      programTitle: 'File Four', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Six': [
    { 
      programTitle: 'File Five', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Seven': [
    { 
      programTitle: 'File Six', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Eight': [
    { 
      programTitle: 'File Seven', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Nine': [
    { 
      programTitle: 'File Eight', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'File Ten': [
    { 
      programTitle: 'File Nine', 
      programCategory: 'English File',
      requiredLevel: 'pass'
    }
  ],
  'C++': [
    {
      programTitle: 'C',
      programCategory: 'Programming Languages',
      requiredLevel: 'pass'
    }
  ],
  'Java': [
    {
      programTitle: 'C++',
      programCategory: 'Programming Languages',
      requiredLevel: 'pass'
    }
  ]
};

/**
 * Initialize the certification service with default criteria
 */
export const initializeCertificationService = () => {
  Object.values(defaultRequirementsByCategory).forEach(criteria => {
    certificationCriteriaStore[`${criteria.programCategory}:*`] = criteria;
  });
  
  console.log('Certification service initialized with default criteria');
  return true;
};

/**
 * Get certification criteria for a program
 */
export const getCertificationCriteria = (programTitle: string, programCategory: string): CertificationCriteria => {
  const specificKey = `${programCategory}:${programTitle}`;
  if (certificationCriteriaStore[specificKey]) {
    return certificationCriteriaStore[specificKey];
  }
  
  const categoryKey = `${programCategory}:*`;
  if (certificationCriteriaStore[categoryKey]) {
    const criteria = {...certificationCriteriaStore[categoryKey]};
    criteria.programTitle = programTitle;
    
    if (programPrerequisites[programTitle]) {
      criteria.prerequisites = programPrerequisites[programTitle];
    }
    
    return criteria;
  }
  
  const defaultCriteria = {...defaultRequirementsByCategory['default']};
  defaultCriteria.programTitle = programTitle;
  defaultCriteria.programCategory = programCategory;
  
  if (programPrerequisites[programTitle]) {
    defaultCriteria.prerequisites = programPrerequisites[programTitle];
  }
  
  return defaultCriteria;
};

/**
 * Set custom certification criteria for a program
 */
export const setCertificationCriteria = (criteria: CertificationCriteria): boolean => {
  const key = criteria.programTitle === '*' 
    ? `${criteria.programCategory}:*`
    : `${criteria.programCategory}:${criteria.programTitle}`;
    
  certificationCriteriaStore[key] = {...criteria};
  return true;
};

/**
 * Initialize student's certification progress for a program
 */
export const initializeStudentCertificationProgress = (
  studentId: number,
  programTitle: string,
  programCategory: string
): StudentCertificationProgress => {
  const criteria = getCertificationCriteria(programTitle, programCategory);
  
  const progressObj: StudentCertificationProgress = {
    studentId,
    programTitle,
    programCategory,
    overallProgress: 0,
    requirementProgress: criteria.requirements.map(req => ({
      requirementId: req.id,
      achieved: 0,
      completed: false
    })),
    prerequisites: {
      met: true,
      missingPrerequisites: []
    },
    updatedAt: safeFormatDate(new Date())
  };
  
  if (!studentCertificationProgressStore[studentId]) {
    studentCertificationProgressStore[studentId] = [];
  }
  
  const existingIndex = studentCertificationProgressStore[studentId]
    .findIndex(p => p.programTitle === programTitle && p.programCategory === programCategory);
    
  if (existingIndex >= 0) {
    studentCertificationProgressStore[studentId][existingIndex] = progressObj;
  } else {
    studentCertificationProgressStore[studentId].push(progressObj);
  }
  
  return progressObj;
};

/**
 * Get a student's certification progress for a program
 */
export const getStudentCertificationProgress = (
  studentId: number,
  programTitle: string,
  programCategory: string
): StudentCertificationProgress | null => {
  if (!studentCertificationProgressStore[studentId]) {
    return null;
  }
  
  const progress = studentCertificationProgressStore[studentId]
    .find(p => p.programTitle === programTitle && p.programCategory === programCategory);
    
  return progress || null;
};

/**
 * Update a requirement's progress for a student's certification
 */
export const updateRequirementProgress = (
  studentId: number,
  programTitle: string,
  programCategory: string,
  requirementId: string,
  achievedValue: number,
  evidence?: string,
  verifiedBy?: string
): StudentCertificationProgress => {
  let progress = getStudentCertificationProgress(studentId, programTitle, programCategory);
  if (!progress) {
    progress = initializeStudentCertificationProgress(studentId, programTitle, programCategory);
  }
  
  const criteria = getCertificationCriteria(programTitle, programCategory);
  
  const requirement = criteria.requirements.find(r => r.id === requirementId);
  if (!requirement) {
    throw new Error(`Requirement ${requirementId} not found for program ${programTitle}`);
  }
  
  const reqIndex = progress.requirementProgress.findIndex(r => r.requirementId === requirementId);
  if (reqIndex >= 0) {
    progress.requirementProgress[reqIndex] = {
      ...progress.requirementProgress[reqIndex],
      achieved: Math.min(achievedValue, requirement.maxValue),
      completed: achievedValue >= requirement.minimumValue,
      verifiedBy: verifiedBy,
      verifiedAt: safeFormatDate(new Date()),
      evidence: evidence
    };
  } else {
    progress.requirementProgress.push({
      requirementId,
      achieved: Math.min(achievedValue, requirement.maxValue),
      completed: achievedValue >= requirement.minimumValue,
      verifiedBy: verifiedBy,
      verifiedAt: safeFormatDate(new Date()),
      evidence: evidence
    });
  }
  
  progress.overallProgress = calculateOverallProgress(progress, criteria);
  
  progress.currentLevel = determineCertificationLevel(progress.overallProgress, criteria);
  
  const studentProgress = studentCertificationProgressStore[studentId];
  const progressIndex = studentProgress
    .findIndex(p => p.programTitle === programTitle && p.programCategory === programCategory);
  
  if (progressIndex >= 0) {
    studentProgress[progressIndex] = progress;
  } else {
    studentProgress.push(progress);
  }
  
  progress.updatedAt = safeFormatDate(new Date());
  
  return progress;
};

/**
 * Update a student's certification status
 */
export const updateCertificationStatus = (
  studentId: number,
  programTitle: string,
  programCategory: string,
  newStatus: CertificationStatus,
  changedBy: string,
  notes?: string
): StudentCertificationProgress => {
  let progress = getStudentCertificationProgress(studentId, programTitle, programCategory);
  if (!progress) {
    progress = initializeStudentCertificationProgress(studentId, programTitle, programCategory);
  }
  
  const statusChange: StatusChangeRecord = {
    previousStatus: progress.certificationStatus,
    newStatus,
    changedAt: safeFormatDate(new Date()),
    changedBy,
    notes
  };
  
  progress.certificationStatus = newStatus;
  
  if (!progress.statusHistory) {
    progress.statusHistory = [];
  }
  progress.statusHistory.push(statusChange);
  
  const studentProgress = studentCertificationProgressStore[studentId];
  const progressIndex = studentProgress
    .findIndex(p => p.programTitle === programTitle && p.programCategory === programCategory);
  
  if (progressIndex >= 0) {
    studentProgress[progressIndex] = progress;
  } else {
    studentProgress.push(progress);
  }
  
  progress.updatedAt = safeFormatDate(new Date());
  
  const certificate = issuedCertificatesStore[studentId]
    .find(cert => cert.programTitle === programTitle && cert.programCategory === programCategory);
  
  if (certificate) {
    trackStatusChange(
      certificate.id,
      certificate.status,
      newStatus,
      changedBy,
      notes
    );
  }
  
  return progress;
};

/**
 * Calculate overall progress based on weighted requirements
 */
const calculateOverallProgress = (
  progress: StudentCertificationProgress,
  criteria: CertificationCriteria
): number => {
  let overallProgress = 0;
  let totalWeight = 0;
  
  progress.requirementProgress.forEach(reqProgress => {
    const requirement = criteria.requirements.find(r => r.id === reqProgress.requirementId);
    if (requirement) {
      const percentAchieved = (reqProgress.achieved / requirement.maxValue) * 100;
      
      overallProgress += percentAchieved * requirement.weight;
      totalWeight += requirement.weight;
    }
  });
  
  return totalWeight > 0 ? overallProgress / totalWeight : 0;
};

/**
 * Determine certification level based on overall progress
 */
const determineCertificationLevel = (
  overallProgress: number,
  criteria: CertificationCriteria
): CertificationLevel | undefined => {
  if (overallProgress >= criteria.distinctionThreshold) {
    return 'distinction';
  } else if (overallProgress >= criteria.meritThreshold) {
    return 'merit';
  } else if (overallProgress >= criteria.passingThreshold) {
    return 'pass';
  }
  
  return undefined;
};

/**
 * Check if a student meets prerequisites for a program
 */
export const checkPrerequisites = (
  studentId: number,
  programTitle: string,
  programCategory: string
): { met: boolean; missingPrerequisites?: ProgramPrerequisite[] } => {
  const criteria = getCertificationCriteria(programTitle, programCategory);
  
  if (!criteria.prerequisites || criteria.prerequisites.length === 0) {
    return { met: true };
  }
  
  const missingPrerequisites: ProgramPrerequisite[] = [];
  
  for (const prereq of criteria.prerequisites) {
    const prereqProgress = getStudentCertificationProgress(
      studentId,
      prereq.programTitle,
      prereq.programCategory || programCategory
    );
    
    if (!prereqProgress || !prereqProgress.currentLevel) {
      missingPrerequisites.push(prereq);
    } else if (prereq.requiredLevel) {
      const meetsLevel = checkLevelRequirement(prereqProgress.currentLevel, prereq.requiredLevel);
      if (!meetsLevel) {
        missingPrerequisites.push(prereq);
      }
    }
  }
  
  return {
    met: missingPrerequisites.length === 0,
    missingPrerequisites: missingPrerequisites.length > 0 ? missingPrerequisites : undefined
  };
};

/**
 * Check if a certification level meets or exceeds required level
 */
const checkLevelRequirement = (
  achievedLevel: CertificationLevel,
  requiredLevel: CertificationLevel
): boolean => {
  const levels = { 'pass': 0, 'merit': 1, 'distinction': 2 };
  return levels[achievedLevel] >= levels[requiredLevel];
};

/**
 * Issue a certificate for a student who has met the requirements
 */
export const issueCertificate = (
  studentId: number,
  programTitle: string,
  programCategory: string,
  issuedBy: string
): CertificateMetadata | null => {
  const progress = getStudentCertificationProgress(studentId, programTitle, programCategory);
  
  if (!progress || !progress.currentLevel) {
    return null;
  }
  
  const prereqCheck = checkPrerequisites(studentId, programTitle, programCategory);
  if (!prereqCheck.met) {
    return null;
  }
  
  const certificateNumber = generateCertificateNumber(studentId, programTitle, programCategory);
  
  const statusChange: StatusChangeRecord = {
    previousStatus: progress.certificationStatus,
    newStatus: 'issued',
    changedAt: safeFormatDate(new Date()),
    changedBy: issuedBy,
    notes: `Certificate issued with number ${certificateNumber}`
  };
  
  const certificate: CertificateMetadata = {
    id: uuidv4(),
    studentId,
    programTitle,
    programCategory,
    certificationLevel: progress.currentLevel,
    issueDate: safeFormatDate(new Date()),
    issuedBy,
    verificationCode: generateVerificationCode(),
    certificateNumber,
    status: 'issued',
    statusHistory: [statusChange]
  };
  
  progress.certificationStatus = 'issued';
  if (!progress.statusHistory) {
    progress.statusHistory = [];
  }
  progress.statusHistory.push(statusChange);
  
  const studentProgress = studentCertificationProgressStore[studentId];
  const progressIndex = studentProgress
    .findIndex(p => p.programTitle === programTitle && p.programCategory === programCategory);
  
  if (progressIndex >= 0) {
    studentProgress[progressIndex] = progress;
  } else {
    studentProgress.push(progress);
  }
  
  if (!issuedCertificatesStore[studentId]) {
    issuedCertificatesStore[studentId] = [];
  }
  issuedCertificatesStore[studentId].push(certificate);
  
  trackCertificateIssuance(certificate, issuedBy);
  
  return certificate;
};

/**
 * Generate a unique certificate number
 */
const generateCertificateNumber = (
  studentId: number,
  programTitle: string,
  programCategory: string
): string => {
  const year = new Date().getFullYear();
  const programCode = programTitle.substring(0, 3).toUpperCase();
  const categoryCode = programCategory.substring(0, 3).toUpperCase();
  const randomCode = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  
  return `${year}-${categoryCode}-${programCode}-${studentId}-${randomCode}`;
};

/**
 * Generate a unique verification code for a certificate
 */
const generateVerificationCode = (): string => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `NALC-${timestamp}-${random}`;
};

/**
 * Get all certificates issued to a student
 */
export const getStudentCertificates = (studentId: number): CertificateMetadata[] => {
  return issuedCertificatesStore[studentId] || [];
};

/**
 * Verify a certificate by its verification code
 */
export const verifyCertificate = (verificationCode: string): CertificateMetadata | null => {
  for (const studentId in issuedCertificatesStore) {
    const certificate = issuedCertificatesStore[studentId].find(
      cert => cert.verificationCode === verificationCode
    );
    
    if (certificate) {
      return certificate;
    }
  }
  
  return null;
};

export const initializeAllCertificationServices = () => {
  initializeCertificationService();
  
  try {
    const { certificateTemplateService } = require('./templates');
    if (certificateTemplateService && certificateTemplateService.initializeCertificateTemplates) {
      certificateTemplateService.initializeCertificateTemplates();
    }
  } catch (err) {
    console.warn('Certificate templates service not available:', err);
  }
  
  return true;
};

export const certificationService = {
  initializeCertificationService,
  initializeAllCertificationServices,
  getCertificationCriteria,
  setCertificationCriteria,
  initializeStudentCertificationProgress,
  getStudentCertificationProgress,
  updateRequirementProgress,
  checkPrerequisites,
  issueCertificate,
  getStudentCertificates,
  verifyCertificate
};

export default certificationService;
